import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ModalBase from "../../../../components/ui/ModalBase";
import { DatePicker } from "../../../../components/shared/DatePicker";
import { workExperienceDateSx } from "../../style";
import useExchangeParameterList from "../../../../hooks/data/useExchangeParameterList";
import colors from "../../../../shared/constants/color";
import axios from "axios";
import { SERVICEURL } from "../../../../appconfig";
import { useToastContext } from "../../../../context/ToastContextProvider";
import {
  getSalaryExpectationDetail,
  updateSalaryExpectation,
} from "../../../../api/salaryExpectationApi";

export interface AddSalaryExpectationModalProps {
  open: boolean;
  memberId: string | number;
  salaryExpectationId?: string | null;
  onClose: () => void;
  onSuccess?: (item: any) => void;
}

const initialFormState = {
  createDate: null as Date | null,
  experienceYear: "",
  experienceMonth: "",
  monthlyNetSalary: "",
  exchangeParameterId: "",
  isInvoiced: false,
};

const AddSalaryExpectationModal: React.FC<AddSalaryExpectationModalProps> = ({
  open,
  memberId,
  salaryExpectationId = null,
  onClose,
  onSuccess,
}) => {
  const isEditMode = !!salaryExpectationId;
  const helperTextSx = { minHeight: 20 };
  const [form, setForm] = useState<typeof initialFormState>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { parameters: exchangeParameters } = useExchangeParameterList();
  const { showToast } = useToastContext();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const resetState = () => {
    setForm(initialFormState);
    setErrors({});
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  useEffect(() => {
    if (!open) {
      // Modal kapandığında state'leri temizle
      resetState();
      return;
    }

    const loadSalaryExpectationData = async () => {
      if (salaryExpectationId) {
        try {
          const data = await getSalaryExpectationDetail(salaryExpectationId);

          setForm({
            createDate: data.createDate ? new Date(data.createDate) : null,
            experienceYear:
              data.totalExperienceYear !== null &&
              data.totalExperienceYear !== undefined
                ? data.totalExperienceYear.toString()
                : "",
            experienceMonth:
              data.totalExperienceMonth !== null &&
              data.totalExperienceMonth !== undefined
                ? data.totalExperienceMonth.toString()
                : "",
            monthlyNetSalary:
              data.salaryExpectation !== null &&
              data.salaryExpectation !== undefined
                ? data.salaryExpectation.toString()
                : "",
            exchangeParameterId:
              data.exchangeParameterId !== null &&
              data.exchangeParameterId !== undefined
                ? data.exchangeParameterId.toString()
                : "",
            isInvoiced: data.isBilling === true || data.isBilling === 1,
          });
        } catch (error) {
          console.error("Salary expectation data loading error:", error);
          showToast(
            "Maaş beklentisi bilgisi yüklenirken bir hata oluştu.",
            "error",
          );
        }
      } else {
        resetState();
      }
    };

    loadSalaryExpectationData();
  }, [open, salaryExpectationId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value === "" || /^[0-9]+$/.test(value)) {
      setForm((prev) => ({ ...prev, [name]: value }));
      if (name === "experienceMonth") {
        const monthNum = Number(value);
        setErrors((prev) => {
          const newErrors = { ...prev };
          if (value !== "" && monthNum > 11) {
            newErrors.experienceMonth = "Ay bilgisi 11'den büyük olamaz";
          } else if (
            newErrors.experienceMonth === "Ay bilgisi 11'den büyük olamaz"
          ) {
            delete newErrors.experienceMonth;
          }
          return newErrors;
        });
      }
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!form.createDate) {
      newErrors.createDate = "Tarih bilgisi zorunludur.";
    }
    if (!form.experienceYear) {
      newErrors.experienceYear = "Deneyim yılı bilgisi zorunludur.";
    }
    if (!form.experienceMonth) {
      newErrors.experienceMonth = "Deneyim ayı bilgisi zorunludur.";
    } else if (Number(form.experienceMonth) > 11) {
      newErrors.experienceMonth = "Ay bilgisi 11'den büyük olamaz";
    }
    if (!form.monthlyNetSalary) {
      newErrors.monthlyNetSalary =
        "Aylık net ücret beklentisi bilgisi zorunludur.";
    }
    if (!form.exchangeParameterId) {
      newErrors.exchangeParameterId = "Ücret tipi seçimi zorunludur.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClick = () => {
    if (!validate()) return;
    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setConfirmOpen(false);

    try {
      setIsSubmitting(true);

      const token = localStorage.getItem("token");
      const currentUserId = localStorage.getItem("memberId");

      const formatDateForApi = (date: Date | null): string | null => {
        if (!date || isNaN(date.getTime())) return null;
        return date.toISOString();
      };

      if (isEditMode && salaryExpectationId) {
        // Düzenleme modu - PUT isteği
        const updatePayload = {
          id: salaryExpectationId,
          salaryExpectation: form.monthlyNetSalary
            ? Number(form.monthlyNetSalary.replace(/\./g, "").replace(",", "."))
            : 0,
          exchangeParameterId: form.exchangeParameterId || undefined,
          totalExperienceYear: form.experienceYear
            ? Number(form.experienceYear)
            : 0,
          totalExperienceMonth: form.experienceMonth
            ? Number(form.experienceMonth)
            : 0,
          enteringMemberId: currentUserId || null,
          isBilling: form.isInvoiced,
          noticeTime: "",
        };

        await updateSalaryExpectation(updatePayload);
        showToast("Maaş beklentisi başarıyla güncellendi.", "success");
      } else {
        // Ekleme modu - POST isteği
        const payload = {
          SalaryExpectation: form.monthlyNetSalary
            ? Number(form.monthlyNetSalary.replace(/\./g, "").replace(",", "."))
            : null,
          ExchangeParameterID: form.exchangeParameterId || null,
          TotalExperienceYear: form.experienceYear
            ? Number(form.experienceYear)
            : null,
          TotalExperienceMonth: form.experienceMonth
            ? Number(form.experienceMonth)
            : null,
          EnteringMemberId: null,
          IsBilling: form.isInvoiced,
          NoticeTime: null,
          CreatedDate: formatDateForApi(form.createDate),
        };

        await axios.post(
          `${SERVICEURL}api/Candidates/AddSalaryExpectationAndNotice/${memberId}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (onSuccess && payload.SalaryExpectation && payload.CreatedDate) {
          const selectedExchange = exchangeParameters.find(
            (p) => String(p.id) === String(form.exchangeParameterId),
          );

          onSuccess({
            salaryExpectation: payload.SalaryExpectation,
            createDate: payload.CreatedDate,
            currencyCode: selectedExchange?.currencyCode || "",
          });
        }

        showToast("Maaş beklentisi başarıyla kaydedildi.", "success");
      }

      handleClose();
    } catch (error: any) {
      console.error("AddSalaryExpectationAndNotice error:", error);
      const message =
        error?.response?.data?.message ||
        "Maaş beklentisi kaydedilirken bir hata oluştu.";
      showToast(message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ModalBase
        open={open}
        onClose={handleClose}
        title={isEditMode ? "Maaş Beklentisi Düzenle" : "Maaş Beklentisi Ekle"}
        actions={
          <>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ borderRadius: 2, textTransform: "none", minWidth: 80 }}
            >
              Kapat
            </Button>
            <Button
              variant="contained"
              sx={{ borderRadius: 2, minWidth: 80 }}
              disabled={isSubmitting}
              onClick={handleSaveClick}
            >
              {isSubmitting ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </>
        }
        maxWidth="lg"
        minWidth={900}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 2,
            width: "100%",
            alignItems: "flex-start",
          }}
        >
          {/* Tarih */}
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <DatePicker
              label="Tarih*"
              value={form.createDate}
              sx={workExperienceDateSx}
              maxDate={new Date()}
              externalError={!!errors.createDate}
              disabled={isEditMode}
              onChange={(newValue: Date | null) => {
                if (newValue) {
                  // Timezone sorununu önlemek için tarihi local midnight'a ayarla
                  const localDate = new Date(newValue);
                  localDate.setHours(12, 0, 0, 0);
                  setForm((prev) => ({
                    ...prev,
                    createDate: localDate,
                  }));
                } else {
                  setForm((prev) => ({
                    ...prev,
                    createDate: null,
                  }));
                }
              }}
              fullWidth
            />
            {errors.createDate && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, ml: 0.5, minHeight: 20 }}
              >
                {errors.createDate}
              </Typography>
            )}
          </Box>

          {/* Deneyim Yıl */}
          <TextField
            fullWidth
            label="Deneyim Yıl*"
            name="experienceYear"
            value={form.experienceYear}
            onChange={handleNumericChange}
            size="small"
            error={!!errors.experienceYear}
            helperText={errors.experienceYear}
            FormHelperTextProps={{ sx: helperTextSx }}
          />

          {/* Deneyim Ay */}
          <TextField
            fullWidth
            label="Deneyim Ay*"
            name="experienceMonth"
            value={form.experienceMonth}
            onChange={handleNumericChange}
            size="small"
            error={!!errors.experienceMonth}
            helperText={errors.experienceMonth}
            FormHelperTextProps={{ sx: helperTextSx }}
          />

          {/* Aylık Net Ücret Beklentisi */}
          <TextField
            fullWidth
            label="Aylık Net Ücret Beklentisi*"
            name="monthlyNetSalary"
            value={form.monthlyNetSalary}
            onChange={handleChange}
            size="small"
            error={!!errors.monthlyNetSalary}
            helperText={errors.monthlyNetSalary}
            FormHelperTextProps={{ sx: helperTextSx }}
          />

          {/* Ücret Tipi */}
          <TextField
            select
            fullWidth
            label="Ücret Tipi*"
            name="exchangeParameterId"
            value={form.exchangeParameterId}
            onChange={handleChange}
            size="small"
            error={!!errors.exchangeParameterId}
            helperText={errors.exchangeParameterId}
            FormHelperTextProps={{ sx: helperTextSx }}
          >
            <MenuItem value="">Seçiniz</MenuItem>
            {exchangeParameters.map((param) => (
              <MenuItem key={param.id} value={param.id}>
                {param.name}
              </MenuItem>
            ))}
          </TextField>

          {/* Faturalı mı? */}
          <FormControlLabel
            control={
              <Checkbox
                checked={form.isInvoiced}
                onChange={handleCheckboxChange}
                name="isInvoiced"
              />
            }
            label="Faturalı mı?"
            sx={{ m: 0 }}
          />
        </Box>
      </ModalBase>
      <Dialog
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      disableScrollLock
    >
        <DialogTitle>
          {isEditMode
            ? "Maaş Beklentisi Güncellensin mi?"
            : "Maaş Beklentisi Eklensin mi?"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {isEditMode
              ? "Maaş Beklentisi güncellemek istediğinize emin misiniz?"
              : "Maaş Beklentisi eklemek istediğinize emin misiniz?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Hayır
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ textTransform: "none", background: colors.blue.B800 }}
          >
            Evet
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddSalaryExpectationModal;
